export type NumberFormat = 'currency' | 'currency2' | 'compact' | 'number' | 'percent' | 'plain'

const currencyWhole = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
})

const currencyCents = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
})

const wholeNumber = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
})

const dateFormat = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
})

const shortDateFormat = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
})

export const formatCurrency = (value: number, cents = false): string => {
    return cents ? currencyCents.format(value) : currencyWhole.format(value)
}

/**
 * Compact currency such as $482.3K or $4M. Written by hand instead of
 * Intl's compact notation, because Node and browsers ship different ICU
 * data and disagree on trailing zeros, which breaks hydration.
 */
export const formatCompactCurrency = (value: number): string => {
    const sign = value < 0 ? '-' : ''
    const absolute = Math.abs(value)
    const units: Array<[number, string]> = [[1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']]

    for (const [size, suffix] of units) {
        if (absolute >= size * 0.9995) {
            const scaled = Math.round((absolute / size) * 10) / 10

            return `${sign}$${scaled.toFixed(1).replace(/\.0$/, '')}${suffix}`
        }
    }

    return `${sign}$${Math.round(absolute)}`
}

export const formatNumber = (value: number, decimals = 0): string => {
    if (decimals === 0) return wholeNumber.format(value)

    return value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })
}

export const formatSignedPercent = (value: number, decimals = 1): string => {
    const sign = value > 0 ? '+' : value < 0 ? '−' : ''

    return `${sign}${Math.abs(value).toFixed(decimals)}%`
}

export const formatValue = (value: number, format: NumberFormat, decimals?: number): string => {
    switch (format) {
        case 'currency':
            return formatCurrency(value)
        case 'currency2':
            return formatCurrency(value, true)
        case 'compact':
            return formatCompactCurrency(value)
        case 'percent':
            return `${value.toFixed(decimals ?? 1)}%`
        case 'plain':
            return value.toFixed(decimals ?? 0)
        default:
            return formatNumber(value, decimals ?? 0)
    }
}

export const formatDate = (value: string | null): string => {
    if (!value) return ''

    const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value)

    if (Number.isNaN(date.getTime())) return ''

    return dateFormat.format(date)
}

export const formatShortDate = (value: string | null): string => {
    if (!value) return ''

    const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value)

    if (Number.isNaN(date.getTime())) return ''

    return shortDateFormat.format(date)
}
